import React, { useEffect, useRef, useState } from 'react';
import { Button } from './ui/button';
import { CheckIcon, XIcon, ArrowDownIcon } from 'lucide-react';

/**
 * LegalDocumentModal — reusable modal for terms & privacy.
 * User must scroll to the bottom before the "Okudum, anladım" button becomes enabled.
 * On confirmation, `onAccept()` is called and the modal closes.
 *
 * Props:
 * - open (bool)
 * - onClose (fn)
 * - onAccept (fn) — called when user confirms
 * - title (string)
 * - markdownContent (string) — plain text with `#` / `##` headings + paragraphs
 * - alreadyAccepted (bool) — if true, hides the confirm button (view-only mode)
 */
export default function LegalDocumentModal({
  open,
  onClose,
  onAccept,
  title,
  markdownContent,
  alreadyAccepted = false,
}) {
  const scrollRef = useRef(null);
  const [reachedBottom, setReachedBottom] = useState(false);

  // Modal her açıldığında scroll pozisyonunu ve durumu sıfırla
  useEffect(() => {
    if (open) {
      setReachedBottom(false);
      // Bir sonraki tick'te scroll'u başa al
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      }, 30);
    }
  }, [open]);

  // ESC ile kapat + body scroll lock
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  const handleScroll = (e) => {
    const el = e.target;
    // 24px'lik tampon: kullanıcı tam en alta gitmese de yakınsa yeter
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 24;
    if (atBottom && !reachedBottom) setReachedBottom(true);
  };

  const scrollToBottom = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  };

  const handleAccept = () => {
    if (!reachedBottom && !alreadyAccepted) return;
    onAccept?.();
    onClose?.();
  };

  // Basit markdown render (# başlık, ## alt başlık, boş satır = paragraf)
  const renderContent = () => {
    const lines = (markdownContent || '').split('\n');
    const blocks = [];
    let paragraph = [];

    const flushPara = () => {
      if (paragraph.length) {
        blocks.push(
          <p key={`p-${blocks.length}`} className="mb-3 text-[13px] leading-relaxed text-gray-700">
            {paragraph.join(' ')}
          </p>
        );
        paragraph = [];
      }
    };

    lines.forEach((raw, i) => {
      const line = raw.trim();
      if (!line) {
        flushPara();
        return;
      }
      if (line.startsWith('# ')) {
        flushPara();
        blocks.push(
          <h2 key={`h1-${i}`} className="mt-6 mb-3 text-lg font-bold text-gray-900 border-b pb-1">
            {line.slice(2)}
          </h2>
        );
        return;
      }
      if (line.startsWith('## ')) {
        flushPara();
        blocks.push(
          <h3 key={`h2-${i}`} className="mt-5 mb-2 text-base font-semibold text-teal-700">
            {line.slice(3)}
          </h3>
        );
        return;
      }
      if (line.startsWith('### ')) {
        flushPara();
        blocks.push(
          <h4 key={`h3-${i}`} className="mt-3 mb-1 text-sm font-semibold text-gray-800">
            {line.slice(4)}
          </h4>
        );
        return;
      }
      if (line.startsWith('- ')) {
        flushPara();
        blocks.push(
          <li key={`li-${i}`} className="ml-5 mb-1 text-[13px] leading-relaxed text-gray-700 list-disc">
            {renderBold(line.slice(2))}
          </li>
        );
        return;
      }
      paragraph.push(line);
    });
    flushPara();
    return blocks;
  };

  // **kalın** metni tanı
  const renderBold = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((p, i) =>
      p.startsWith('**') && p.endsWith('**') ? (
        <strong key={i} className="font-semibold text-gray-900">
          {p.slice(2, -2)}
        </strong>
      ) : (
        <React.Fragment key={i}>{p}</React.Fragment>
      )
    );
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
      data-testid="legal-modal-overlay"
    >
      <div
        className="relative w-full max-w-3xl max-h-[90vh] bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Başlık */}
        <div className="flex items-center justify-between border-b px-6 py-4 bg-gradient-to-r from-teal-50 to-emerald-50">
          <div>
            <h2 className="text-xl font-bold text-gray-900" data-testid="legal-modal-title">{title}</h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Lütfen aşağıya kadar okuyun. Okuduktan sonra alt kısımdaki onay butonu aktifleşecek.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Kapat"
            data-testid="legal-modal-close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* İçerik — scroll edilebilir */}
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto px-6 py-5 relative"
          data-testid="legal-modal-scroll"
        >
          {renderContent()}
          <div className="mt-6 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-center">
            <p className="text-sm font-medium text-emerald-800">
              ✓ Metnin sonuna ulaştınız
            </p>
          </div>
        </div>

        {/* Scroll'un altına inildiğini bilmiyorsa altta ok göster */}
        {!reachedBottom && !alreadyAccepted && (
          <button
            type="button"
            onClick={scrollToBottom}
            className="absolute bottom-24 right-6 bg-teal-600 text-white rounded-full p-2.5 shadow-lg hover:bg-teal-700 animate-bounce"
            title="Aşağı in"
            data-testid="legal-modal-scroll-down"
          >
            <ArrowDownIcon className="w-4 h-4" />
          </button>
        )}

        {/* Alt aksiyon barı */}
        <div className="border-t bg-gray-50 px-6 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-gray-500">
            {reachedBottom || alreadyAccepted ? (
              <span className="text-emerald-700 font-medium inline-flex items-center gap-1">
                <CheckIcon className="w-4 h-4" /> Metni okudunuz
              </span>
            ) : (
              <span>Onaylamak için metnin sonuna kadar okumalısınız</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="legal-modal-cancel"
            >
              Kapat
            </Button>
            {!alreadyAccepted && (
              <Button
                type="button"
                onClick={handleAccept}
                disabled={!reachedBottom}
                className="bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="legal-modal-accept"
              >
                <CheckIcon className="w-4 h-4 mr-1" /> Okudum, Anladım
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
